// ===================== HUD (DOM overlay) =====================
import { Elements } from './registries.js';
import { PASSIVES } from '../content/upgrades.js';

const passiveById = Object.fromEntries(PASSIVES.map((p) => [p.id, p]));

function fmtTime(s) {
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return String(m).padStart(2, '0') + ':' + String(sec).padStart(2, '0');
}

export class HUD {
  constructor() {
    this.el = {
      hud: document.getElementById('hud'),
      xpFill: document.getElementById('xp-fill'),
      xpLabel: document.getElementById('xp-label'),
      timer: document.getElementById('timer'),
      stage: document.getElementById('stage-name'),
      hpFill: document.getElementById('hp-fill'),
      hpLabel: document.getElementById('hp-label'),
      kills: document.getElementById('kills'),
      gold: document.getElementById('gold'),
      element: document.getElementById('element-badge'),
      weaponRow: document.getElementById('weapon-row'),
      bossBar: document.getElementById('boss-bar'),
      bossName: document.getElementById('boss-name'),
      bossFill: document.getElementById('boss-fill'),
    };
    this._sig = '';
    // Where the HUD chrome actually ends, in CSS px. Canvas-drawn overlays
    // (the combo counter) read it off `game.hudBottom` so they can clear the
    // DOM HUD at any viewport instead of assuming a fixed height.
    this.bottomPx = 0;
    this._mw = 0; this._mh = 0; this._mSig = null;
  }

  show() { this.el.hud.classList.remove('hidden'); }
  hide() { this.el.hud.classList.add('hidden'); }

  update(game) {
    if (!game.player) return;
    const p = game.player;
    const e = this.el;

    e.xpFill.style.width = (100 * p.xp / p.xpToNext) + '%';
    e.xpLabel.textContent = 'LV ' + p.level;
    e.timer.textContent = fmtTime(game.elapsed);

    // Where you are (the world-map hex, or the stage itself) + time until the
    // finale. Once it lands the line names it — read off the live boss, since
    // each region has its own finale and the HUD must not name content.
    const place = ((game.node && game.node.nodeName) || game.stage.name)
      + (game.threat ? ` ☠${game.threat}` : '');
    const remain = Math.max(0, (game.duration || 0) - game.elapsed);
    e.stage.textContent = game.overrun
      ? place + ' · 🌀 OVERRUN +' + fmtTime(game.elapsed - game.overrunStart)
      : game.reaperSpawned
        ? place + ' · ⚰ ' + ((game.reaper && game.reaper.name) || 'THE FINALE')
        : place + ' · ' + fmtTime(remain) + ' left';

    const hpPct = Math.max(0, 100 * p.hp / p.maxHp);
    e.hpFill.style.width = hpPct + '%';
    e.hpLabel.textContent = Math.max(0, Math.ceil(p.hp)) + ' / ' + Math.round(p.maxHp);
    e.hpFill.parentElement.classList.toggle('low', hpPct < 32 && hpPct > 0);

    e.kills.textContent = '☠ ' + game.kills;
    e.gold.textContent = '◈ ' + game.gold;

    const el = Elements.get(p.element);
    // Tier-2 (Ascendant) elements have no level ladder left — show a star.
    e.element.textContent = el.short + ' ' + el.name +
      (p.element !== 'neutral' ? (el.tier === 2 ? ' ★' : ' L' + p.elementLevel) : '');
    e.element.style.color = el.color;

    // VS-style slot rows: 6 weapon slots + 6 passive slots. Rebuild on change.
    // The Multiplication slot carries the combined effective ×, so a golden
    // pickup has to move the signature too — it changes the readout without
    // changing any passive level.
    const passArr = p.passiveSlots();
    const sig = p.weapons.map((w) => w.def.id + w.level).join(',') + '|' +
      passArr.map((s) => s.id + s.label).join(',');
    if (sig !== this._sig) {
      this._sig = sig;
      // Ultraweapons can push past the 6 core slots — the row grows with them.
      const slotCount = Math.max(6, p.weapons.length);
      let wHtml = '';
      for (let i = 0; i < slotCount; i++) {
        const w = p.weapons[i];
        wHtml += w
          ? `<div class="slot${w.def.ultra ? ' ultra' : ''}"><span class="slot-icon">${w.def.icon}</span><span class="lvl">${w.level}</span></div>`
          : '<div class="slot empty"></div>';
      }
      let pHtml = '';
      const pSlots = Math.max(slotCount, passArr.length);
      for (let i = 0; i < pSlots; i++) {
        const entry = passArr[i];
        if (entry) {
          const pa = passiveById[entry.id];
          const icon = pa ? pa.icon : (entry.id === 'multiply' ? '✖' : '◆');
          const cls = entry.id === 'multiply' || entry.id === 'duplicate' ? 'slot tag-multiply' : 'slot passive';
          const lvlCls = entry.id === 'multiply' ? 'lvl mult' : 'lvl';
          pHtml += `<div class="${cls}"><span class="slot-icon">${icon}</span><span class="${lvlCls}">${entry.label}</span></div>`;
        } else {
          pHtml += '<div class="slot empty"></div>';
        }
      }
      e.weaponRow.innerHTML = `<div class="slot-row">${wHtml}</div><div class="slot-row">${pHtml}</div>`;
    }

    // Boss bar (Alpha super bosses burn gold).
    if (game.boss && !game.boss.dead) {
      e.bossBar.classList.remove('hidden');
      // An Alpha is marked with ★ as well as the gold bar — the gold and the
      // ordinary magenta→orange bar both simulate toward khaki, so the colour
      // swap alone doesn't tell a red/green-blind player this one is worse.
      e.bossName.textContent = (game.boss.super ? '★ ' : '') + (game.boss.name || 'BOSS');
      e.bossFill.style.width = Math.max(0, 100 * game.boss.hp / game.boss.maxHp) + '%';
      e.bossFill.style.background = game.boss.super ? 'linear-gradient(90deg, #ffd15c, #ff8a3d)' : '';
    } else {
      e.bossBar.classList.add('hidden');
    }

    // Publish the bottom of the HUD cluster. The top rows wrap on narrow
    // viewports and the slot rows grow with ultraweapons, so the height is not
    // a constant. Measured only when the layout can have moved — on a resize
    // or a slot-row rebuild — never per frame, since this forces a reflow.
    const vw = window.innerWidth, vh = window.innerHeight;
    if (vw !== this._mw || vh !== this._mh || sig !== this._mSig) {
      this._mw = vw; this._mh = vh; this._mSig = sig;
      const r = e.weaponRow.getBoundingClientRect();
      if (r.height > 0) {
        this.bottomPx = r.bottom;
        // Published to CSS as well: the achievement toasts have to clear the
        // same chrome, and were doing it with a hardcoded 232px that the
        // wrapped slot rows outgrew. One measurement, both consumers.
        document.documentElement.style.setProperty('--hud-bottom', this.bottomPx + 'px');
      }
    }
    game.hudBottom = this.bottomPx;
  }
}
